import { vehicleFieldApplies, VehicleIdentity } from '../catalog/applicability';
import type { CatalogCar } from '../catalog/model';

/** Highest-priced available cars: distinct makes, then distinct models, then fill. */
export function selectCatalogShowcase(cars: CatalogCar[]): CatalogCar[] {
  const identity = (value: string | null | undefined) =>
    value?.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase() ?? '';
  const modelKey = (car: CatalogCar) => {
    const make = identity(car.make),
      model = identity(car.model);
    return make && model ? JSON.stringify([make, model]) : undefined;
  };
  const compare = (a: CatalogCar, b: CatalogCar) =>
    b.amount! - a.amount! || (a.mobileAdId < b.mobileAdId ? -1 : a.mobileAdId > b.mobileAdId ? 1 : 0);
  const ranked = cars.filter(car => hasCatalogPrice(car) && car.amount! >= 0).sort(compare);
  const selected: CatalogCar[] = [];
  const ids = new Set<string>(),
    makes = new Set<string>(),
    models = new Set<string>();
  const add = (car: CatalogCar) => {
    selected.push(car);
    ids.add(car.mobileAdId);
    makes.add(identity(car.make));
    const model = modelKey(car);
    if (model) models.add(model);
  };
  for (const preference of ['make', 'model', 'any'] as const) {
    for (const car of ranked) {
      if (selected.length === 3) break;
      if (ids.has(car.mobileAdId)) continue;
      const make = identity(car.make),
        model = modelKey(car);
      if (preference === 'make' && (!make || makes.has(make))) continue;
      if (preference === 'model' && (!model || models.has(model))) continue;
      add(car);
    }
  }
  return selected.sort(compare);
}

export function hasCatalogPrice(car: { amount?: number | null; priceType?: string | null }): boolean {
  return typeof car.amount === 'number' && Number.isFinite(car.amount) && car.priceType !== 'ON_REQUEST';
}

export function formatCatalogPrice(
  amount: number | null | undefined,
  priceType: string | null | undefined,
  locale: string,
  onRequest: string,
  currency?: string | null
): string {
  return hasCatalogPrice({ amount, priceType })
    ? `${amount!.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: currency && currency !== 'EUR' ? 2 : 0
      })} ${!currency || currency === 'EUR' ? '€' : currency}`
    : onRequest;
}

export function catalogLanguagePath(
  pathname: string,
  currentLocale: string,
  targetLocale: string,
  availableLocales?: string[]
): string {
  const currentLang = currentLocale.split('-')[0];
  const targetLang = targetLocale.split('-')[0];
  let basePath = currentLang === 'de' ? pathname : pathname.replace(new RegExp(`^/${currentLang}(?=/|$)`), '') || '/';
  if (availableLocales && /^\/cars\/[^/]+\/?$/.test(basePath) && !availableLocales.includes(targetLocale))
    basePath = '/cars';
  return targetLang === 'de' ? basePath : basePath === '/' ? `/${targetLang}` : `/${targetLang}${basePath}`;
}

export function catalogTechnicalValues(
  car: { power?: number | null; cubicCapacity?: number | null } & VehicleIdentity,
  locale: string
) {
  const format = (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 0 });
  return {
    power:
      !vehicleFieldApplies(car, 'power') || car.power == null
        ? undefined
        : `${format(car.power)} kW (${format(car.power * 1.36)} PS)`,
    cubicCapacity:
      !vehicleFieldApplies(car, 'cubicCapacity') || car.cubicCapacity == null
        ? undefined
        : `${format(car.cubicCapacity)} ccm`
  };
}

export function filterCatalogLocalePaths(
  paths: Record<string, string>,
  availableLocales?: string[]
): Record<string, string> {
  if (availableLocales === undefined) return paths;
  return Object.fromEntries(
    Object.entries(paths).filter(([lang]) => availableLocales.some(locale => locale.split('-')[0] === lang))
  );
}

/** Provider net amount takes precedence; derive only from two actual valid numeric facts. */
export function catalogNetPrice(car: {
  amount?: number | null;
  vatRate?: number | null;
  details?: { values: Record<string, unknown> };
}): number | undefined {
  const raw = car.details?.values['price.consumerPriceNet'];
  if (
    ((typeof raw === 'string' && /^\d+(?:\.\d+)?$/.test(raw)) || typeof raw === 'number') &&
    Number.isFinite(Number(raw)) &&
    Number(raw) >= 0
  )
    return Number(raw);
  return typeof car.amount === 'number' &&
    Number.isFinite(car.amount) &&
    car.amount >= 0 &&
    typeof car.vatRate === 'number' &&
    Number.isFinite(car.vatRate) &&
    car.vatRate >= 0
    ? car.amount / (1 + car.vatRate)
    : undefined;
}
